import { useEffect, useRef } from "react";

/**
 * Hook de défilement infini basé sur IntersectionObserver.
 *
 * Observe un élément sentinelle (à placer en bas de la liste) et déclenche
 * `onLoadMore` lorsqu'il devient visible, tant qu'il reste des données à charger.
 *
 * @param {Object} params
 * @param {boolean} params.hasMore - Indique s'il reste des données à charger.
 * @param {boolean} params.loading - Indique qu'un chargement est déjà en cours.
 * @param {() => void} params.onLoadMore - Callback déclenché en bas de liste.
 * @param {string} [params.rootMargin="200px"] - Marge de pré-chargement.
 * @returns {React.RefObject<HTMLElement>} Ref à attacher à l'élément sentinelle.
 */
export const useInfiniteScroll = ({
  hasMore,
  loading,
  onLoadMore,
  rootMargin = "200px",
}) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || loading) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // On se désabonne immédiatement pour éviter les déclenchements
          // multiples ; l'effet se ré-exécutera au prochain rendu utile.
          observer.disconnect();
          onLoadMore();
        }
      },
      { rootMargin },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore, rootMargin]);

  return sentinelRef;
};
